import React, { useState } from 'react';
import { 
  X, 
  MousePointer2, 
  Monitor, 
  Laptop, 
  Lock, 
  Unlock, 
  Zap, 
  Sparkles, 
  ShieldCheck, 
  Layers, 
  ArrowLeftRight, 
  CheckCircle2, 
  Sliders, 
  Gamepad2,
  Download
} from 'lucide-react';
import { CkLogo } from './CkLogo';
import { DeviceInfo } from '../types';

interface FlowDeskModalProps {
  isOpen: boolean;
  onClose: () => void;
  peers: DeviceInfo[];
  currentDevice: DeviceInfo;
  onOpenDownloads: () => void;
}

export const FlowDeskModal: React.FC<FlowDeskModalProps> = ({
  isOpen,
  onClose,
  peers,
  currentDevice,
  onOpenDownloads
}) => {
  const [isLocked, setIsLocked] = useState(false);
  const [sensitivity, setSensitivity] = useState<'immediate' | 'normal' | 'sticky'>('normal');
  const [activeScreenIndex, setActiveScreenIndex] = useState(0);

  if (!isOpen) return null;

  // Combine current device with connected peers for desk layout preview
  const deskScreens = [
    { id: currentDevice.deviceId, name: currentDevice.deviceName || 'This PC (Host)', os: currentDevice.os, isHost: true },
    ...peers.map((p) => ({ id: p.deviceId, name: p.deviceName || 'Client PC', os: p.os, isHost: false }))
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 bg-black/85 backdrop-blur-2xl animate-fadeIn">
      
      {/* Container */}
      <div className="relative w-full max-w-4xl max-h-[92vh] flex flex-col bg-[#070912]/95 border border-cyan-500/30 rounded-3xl shadow-[0_25px_80px_rgba(0,0,0,0.9),0_0_50px_rgba(0,240,255,0.15)] overflow-hidden">
        
        {/* Glows */}
        <div className="absolute -top-32 -left-32 w-80 h-80 bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-80 h-80 bg-purple-600/15 rounded-full blur-3xl pointer-events-none" />

        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 sm:px-8 py-4 sm:py-5 border-b border-white/10 bg-white/[0.02] relative z-10">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 shadow-glow-cyan">
              <MousePointer2 size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2.5">
                <h2 className="text-xl sm:text-2xl font-extrabold text-white font-display tracking-tight">
                  CK <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">Flow</span>
                </h2>
                <span className="px-2.5 py-0.5 text-[10px] font-bold font-mono text-cyan-300 bg-cyan-500/15 border border-cyan-500/30 rounded-full">
                  1 Mouse • 3 PCs
                </span>
              </div>
              <p className="text-xs text-slate-400 hidden sm:block">Seamless cursor &amp; keyboard gliding across all computers on your desk</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-2xl hover:bg-white/10 transition-colors cursor-pointer"
            title="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-7 space-y-6">
          
          {/* Interactive Desk Layout Visualizer */}
          <div className="p-6 rounded-3xl bg-gradient-to-b from-white/[0.03] to-black/60 border border-white/10 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Layers size={16} className="text-cyan-400" />
                <h3 className="text-sm font-bold text-white uppercase tracking-wider font-display">
                  Virtual Desk Monitor Topology
                </h3>
              </div>

              <div className="flex items-center gap-2 text-xs">
                <button
                  onClick={() => setIsLocked(!isLocked)}
                  className={`px-3 py-1.5 rounded-xl border flex items-center gap-1.5 font-bold transition-all cursor-pointer ${
                    isLocked
                      ? 'bg-rose-500/20 border-rose-500/40 text-rose-300 shadow-[0_0_15px_rgba(244,63,94,0.2)]'
                      : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                  }`}
                >
                  {isLocked ? <Lock size={13} /> : <Unlock size={13} />}
                  <span>{isLocked ? 'Gaming Locked (Ctrl+Ctrl)' : 'Flow Active (Unlocked)'}</span>
                </button>
              </div>
            </div>

            {/* Visual Monitors Horizontal Ribbon */}
            <div className="p-6 rounded-2xl bg-[#060810] border border-white/5 flex items-center justify-center gap-4 flex-wrap min-h-[160px] relative overflow-hidden">
              {deskScreens.map((screen, idx) => {
                const isActive = activeScreenIndex === idx;
                return (
                  <div
                    key={screen.id + idx}
                    onClick={() => setActiveScreenIndex(idx)}
                    className={`relative w-48 sm:w-56 p-4 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between ${
                      isActive
                        ? 'bg-gradient-to-b from-cyan-950/40 to-purple-950/30 border-cyan-400 shadow-[0_0_25px_rgba(0,240,255,0.25)] scale-105'
                        : 'bg-white/[0.02] border-white/10 hover:border-white/25 hover:bg-white/[0.04]'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className={`p-2 rounded-xl border ${isActive ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40' : 'bg-white/5 text-slate-400 border-white/10'}`}>
                        {screen.os === 'windows' ? <Monitor size={18} /> : <Laptop size={18} />}
                      </div>
                      <span className={`text-[9px] font-mono font-bold px-2 py-0.5 rounded-full ${screen.isHost ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' : 'bg-white/5 text-slate-400'}`}>
                        {screen.isHost ? 'Master Mouse' : `Screen ${idx + 1}`}
                      </span>
                    </div>

                    <div>
                      <h4 className="text-xs font-bold text-white truncate">{screen.name}</h4>
                      <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                        {isActive ? '🟢 Active Focus' : 'Move cursor to edge ➔'}
                      </p>
                    </div>

                    {/* Edge Flow Indicator */}
                    {idx < deskScreens.length - 1 && (
                      <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-cyan-500/20 border border-cyan-400/40 items-center justify-center text-cyan-300 z-10 pointer-events-none">
                        <ArrowLeftRight size={11} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <p className="text-[11px] text-slate-400 text-center">
              Move your mouse past the screen edge to glide onto the next PC. Double-tap <strong className="text-white font-mono">Ctrl</strong> anytime to lock cursor during games.
            </p>
          </div>

          {/* Quick Setup & Key Capabilities Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            
            <div className="p-4 rounded-2xl bg-black/40 border border-white/5 space-y-2">
              <div className="flex items-center gap-2 text-cyan-400">
                <Zap size={16} />
                <h4 className="text-xs font-bold text-white">Sub-1ms UDP Mesh</h4>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Direct 16-byte binary UDP datagrams over your home Wi-Fi/Ethernet for 144Hz buttery smooth mouse tracking.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-black/40 border border-white/5 space-y-2">
              <div className="flex items-center gap-2 text-purple-400">
                <Gamepad2 size={16} />
                <h4 className="text-xs font-bold text-white">Gaming Lock Mode</h4>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Quickly double-tap <strong className="text-white font-mono">Ctrl</strong> or press <strong className="text-white font-mono">Ctrl+Alt+L</strong> to lock mouse to current screen.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-black/40 border border-white/5 space-y-2">
              <div className="flex items-center gap-2 text-emerald-400">
                <ShieldCheck size={16} />
                <h4 className="text-xs font-bold text-white">Zero Configuration</h4>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                No IP address typing or router port forwarding. Just join the same CK Room on all computers.
              </p>
            </div>

          </div>

          {/* Pricing & Remote PRO Strip */}
          <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-emerald-950/30 via-purple-950/30 to-black/60 border border-cyan-500/20 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
            <div className="space-y-1 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-2">
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-bold text-[10px]">
                  LOCAL LAN: 100% FREE
                </span>
                <span className="text-white font-bold">Remote PRO: $9.99/yr or $29.99 Lifetime</span>
              </div>
              <p className="text-[11px] text-slate-400">
                Local Wi-Fi mesh is always free. For remote internet multi-PC control, message founder on WhatsApp: <strong className="text-emerald-300">+91 7600477225</strong>
              </p>
            </div>

            <div className="flex items-center gap-2">
              <a
                href="https://wa.me/917600477225?text=Hi%20Chintan!%20I%20want%20to%20buy%20CK%20Flow%20PRO%20(Lifetime%20$29.99)."
                target="_blank"
                rel="noopener noreferrer"
                className="px-3.5 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 shadow-md transition-all cursor-pointer whitespace-nowrap"
              >
                💬 Buy on WhatsApp
              </a>

              <button
                onClick={() => {
                  onClose();
                  onOpenDownloads();
                }}
                className="px-3.5 py-2 rounded-xl text-xs font-bold text-black bg-gradient-to-r from-cyan-400 to-teal-400 hover:from-cyan-300 hover:to-teal-300 shadow-lg transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                Get Free Local App
              </button>
            </div>
          </div>


        </div>

      </div>
    </div>
  );
};
